// import assert from 'assert';
// import flatMap from 'lodash/flatMap';
// import isFunction from 'lodash/isFunction';
// import {
//   joinPartialPaths,
//   isRef,
//   parseRef,
//   watchValues,
//   getValues,
//   getValueType,
// } from '../../muster-utils';


const assert =require('assert');
const flatMap =require('lodash/flatMap');
const isFunction =require('lodash/isFunction');
const {
  joinPartialPaths,
  isRef,
  parseRef,
  watchValues,
  getValues,
  getValueType,
} =require('../../muster-utils');

/**
 * Create a wildcard handler that redirects all queries under the specified node to a different
 * graph node, located by querying a lookup key from another point in the graph.
 * @param {Ref|Function|Array<Ref|Function>} lookup Path to the graph node containing the lookup key
 * @param {function(...key: any): Path} transform Transform function to convert the lookup key node
 * value(s) to a reference to the target branch path
 * @returns {RouteHandlerDefinition} Wildcard route handler definition
 * @example
 * const USERS = [
 *  { firstName: 'Alice', lastName: 'Jones' },
 *  { firstName: 'Bob', lastName: 'Smith' },
 * ];
 * const DEFAULT_USER = 0;
 * const DISPLAY_FIELD = 'name'
 * muster()
 *   .match('selectedUser.*', redirectOn(ref('selectedUserId'), (id) => `users[${id}]`))
 *   .path('selectedUserId', variable(DEFAULT_USER));
 *
 *   .path('selectedUserDisplay', redirectOn([ref('selectedUserId'), ref('displayField') ],
 *     (id, field) => `users[${id}][${field}]`
 *   ))
 *   .match('users[{integers:id}].name', requests(({ req, res }) =>
 *     res.value(USERS[req.params.id].name)
 *   ))
 *
 *  .match('sortedUsers[{integers:index}].*', redirectOn(
 *    ({ req, res }) => `sortedUserIds[${req.params.index}]`,
 *    (id) => `usersById[${id}]`
 *  ))
 *  .match('sortedUserIds[{integers:index}]', ({ req, res }) => res.value(...))
 *  .match('usersById[{keys:id}].*', ({ req, res }) => {...});
 *
 *  .match('sortedUsers[{integers:index}].*', redirectOn(
 *    [
 *      ({ req, res }) => `sortedUserIds[{req.params.index}]`,
 *      ({ req, res }) => `foos[{req.params.index}]`,
 *    ],
 *    (id, foo) => `usersByIdAndFoo[${id}][${foo}]`
 *  ))
))
 */
function redirectOn(lookup, transform) {
  assert(lookup, 'No redirectOn lookup graph node specified');
  assert((
    isRef(lookup) ||
    isFunction(lookup) ||
    (Array.isArray(lookup) && lookup.every((value) => isRef(value) || isFunction(value)))
  ),
    'Invalid redirectOn graph lookup' +
    '\n Expected:' +
    '\n  ref' +
    '\n  Function' +
    '\n  Array<ref>' +
    '\n  Array<Function>' +
    '\n Received:' +
    `\n  ${getValueType(lookup)}`
  );
  assert(!Array.isArray(lookup) || Boolean(transform),
    'Multiple redirectOn graph lookups require a path transform function'
  );
  const lookups = (Array.isArray(lookup) ? lookup : [lookup]).map(parseLookup);
  return {
    get(requests, { get }) {
      const lookupPaths = flatMap(lookups, (withRequests) => withRequests(requests));
      return watchValues(get(lookupPaths))
        .map((lookupValues) => {
          const targetBranchPath = transform ? transform(...lookupValues) : lookupValues[0];
          if (!Array.isArray(requests)) {
            const { res } = requests;
            return res.redirect(targetBranchPath);
          }
          return requests.map(
            ({ req, res }) => res.redirect(joinPartialPaths(targetBranchPath, req.params['*']))
          );
        });
    },
    set(requests, { get }) {
      const lookupPaths = flatMap(lookups, (withRequests) => withRequests(requests));
      return getValues(get(lookupPaths))
        .then((lookupValues) => {
          const targetBranchPath = transform ? transform(...lookupValues) : lookupValues[0];
          if (!Array.isArray(requests)) {
            const { res } = requests;
            return res.redirect(targetBranchPath);
          }
          return requests.map(
            ({ req, res }) => res.redirect(joinPartialPaths(targetBranchPath, req.params['*']))
          );
        });
    },
    call({ req, res }, { get, call }) {
      const lookupPaths = flatMap(lookups, (withRequests) => withRequests([req, res]));
      return getValues(get(lookupPaths))
        .then((lookupValues) => {
          const targetBranchPath = transform ? transform(...lookupValues) : lookupValues[0];
          return call(joinPartialPaths(targetBranchPath, req.params['*']), ...req.args)
            .then((response) => ({ response }));
        });
    },
  };
}

function parseLookup(lookup) {
  return (requests) => {
    if (isFunction(lookup)) {
      return Array.isArray(requests) ? requests.map(lookup) : lookup(requests);
    }
    return parseRef(lookup);
  };
}

module.exports = redirectOn;
